import React, { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import Typography from '@mui/material/Typography';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import MenuItem from '@mui/material/MenuItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import EditIcon from '@mui/icons-material/Edit';
import { styled } from '@mui/material/styles';
import { Box } from '@mui/material';
import { DEFAULT_SHADOW } from 'styles/theme';
import { capitalize } from 'helpers/helpers';
import IconMenu from 'components/IconMenu';
import TextEditor from 'ui/TextEditor/TextEditor';
import { BUDGET } from 'business/constants';
import { TRACKABLE_EVENTS, trackEventBudget } from 'helpers/trackerHelpers';
import { EnrichedOperationT } from './helpers/types';
import { useEnrichedOperationMutation } from './helpers/hooks';
import { PADDING_TITLE } from './helpers/constants';

const StyDescriptionContainer = styled(Box, {
    name: 'StyDescriptionContainer',
})({
    ...DEFAULT_SHADOW,
    position: 'relative',
    minHeight: '8em',
});

const StyIconMenu = styled(IconMenu, {
    name: 'StyIconMenu',
})({
    position: 'absolute',
    inset: '8px 8px auto auto',
    zIndex: 1,
    display: 'flex',
    justifyContent: 'flex-end',
    backgroundColor: 'white',
    '&:hover': { backgroundColor: '#fafafa' },
});

export default function OperationDescription({
    enrichedOperation,
}: {
    enrichedOperation: EnrichedOperationT;
}): React.ReactElement {
    const { t } = useTranslation();
    const queryClient = useQueryClient();
    const [isEditable, setIsEditable] = useState<boolean>(false);
    const { mutate: onDescriptionUpdated } =
        useEnrichedOperationMutation(queryClient);
    const onSave = useCallback(
        (html: string) => {
            setIsEditable(false);
            onDescriptionUpdated({
                operationSoftwareId: enrichedOperation.softwareId,
                description: html,
            });
        },
        [enrichedOperation.softwareId],
    );

    const menu = !isEditable && enrichedOperation.canEdit && (
        <StyIconMenu
            iconElement={<MoreVertIcon />}
            tooltip={capitalize(t('common.options'))}
            tooltipPlacement="bottom"
            small
        >
            <MenuItem onClick={() => setIsEditable(true)}>
                <ListItemIcon>
                    <EditIcon />
                </ListItemIcon>
                {capitalize(t('common.edit'))}
            </MenuItem>
        </StyIconMenu>
    );

    const style = {
        padding: '0.5rem 0.5rem 1.5rem',
    };

    return (
        <div>
            <Typography sx={{ ...PADDING_TITLE }} variant="h5">
                {t('secondaryBudgetAxisElement360.description')}
            </Typography>
            <StyDescriptionContainer>
                {menu}
                <TextEditor
                    isEditable={isEditable}
                    content={enrichedOperation.description}
                    onSave={onSave}
                    canEdit={enrichedOperation.canEdit}
                    style={style}
                    module={BUDGET}
                    trackerFunctions={{
                        trackMentionCharClicked: () =>
                            trackEventBudget(
                                TRACKABLE_EVENTS.budget.general.operation360
                                    .clickMentionCharTool,
                            ),
                        trackMentionCreated: () =>
                            trackEventBudget(
                                TRACKABLE_EVENTS.budget.general.operation360
                                    .addDescriptionMention,
                            ),
                    }}
                />
            </StyDescriptionContainer>
        </div>
    );
}
